// frontend/src/EventDetail.js
import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import './EventDetail.css';
import { FaPencilAlt, FaSave, FaTimes } from 'react-icons/fa';
import PlacesAutocomplete, { geocodeByAddress, getLatLng } from 'react-places-autocomplete';
import PaymentComponent from './PaymentComponent';

function getCookie(name) {
  let cookieValue = null;
  if (document.cookie && document.cookie !== '') {
    const cookies = document.cookie.split(';');
    for (let cookie of cookies) {
      cookie = cookie.trim();
      if (cookie.substring(0, name.length + 1) === name + '=') {
        cookieValue = decodeURIComponent(cookie.substring(name.length + 1));
        break;
      }
    }
  }
  return cookieValue;
}

function utcToLocalInputValue(utcString) {
  const dateObj = new Date(utcString);
  const tzOffset = dateObj.getTimezoneOffset() * 60000;
  const localTime = new Date(dateObj.getTime() - tzOffset);
  return localTime.toISOString().slice(0, 16);
}

function localInputValueToUtc(localString) {
  const localDate = new Date(localString);
  const tzOffset = localDate.getTimezoneOffset() * 60000;
  const utcDate = new Date(localDate.getTime() + tzOffset);
  return utcDate.toISOString();
}

function EventDetail() {
  const [user, setUser] = useState(null); // current user details
  const { id } = useParams();
  const navigate = useNavigate();

  const [event, setEvent] = useState(null);
  const [statusMsg, setStatusMsg] = useState('');
  const [showShare, setShowShare] = useState(false);
  const [eventUrl, setEventUrl] = useState(window.location.href);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [chatMessages, setChatMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [pendingJoinRequests, setPendingJoinRequests] = useState([]); // NEW: pending join requests
  const [showPayment, setShowPayment] = useState(false);

const [editFormData, setEditFormData] = useState({
  name: '',
  description: '',
  location: '',
  event_time: '',
  elevation_diff: '',
  distance: '',
  estimated_duration: '',
  price: '',
});

  const handleFileSelect = (e) => {
    setSelectedFile(e.target.files[0]);
  };

  // Fetch user and event details on mount
  useEffect(() => {
    // Fetch user details
    fetch('/api/user/', { credentials: 'include' })
      .then((res) => res.ok ? res.json() : null)
      .then((data) => setUser(data))
      .catch((err) => console.error('Error fetching user:', err));

    // Fetch event details
    fetch(`/api/events/${id}/`, {
      method: 'GET',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
    })
      .then((response) => {
        if (!response.ok) throw new Error('Failed to fetch event detail');
        return response.json();
      })
	  .then((data) => {
	  const localInputValue = utcToLocalInputValue(data.event_time);
	  setEvent(data);
	  setEditFormData({
	    name: data.name || '',
	    description: data.description || '',
	    location: data.location || '',
	    event_time: localInputValue,
	    elevation_diff: data.elevation_diff || '',
	    distance: data.distance || '',
	    estimated_duration: data.estimated_duration || '',
	    price: data.price || '',
	  });
	})

      .catch((err) => console.error(err));
  }, [id]);

  // NEW: Load pending join requests (only if the logged-in user is the organizer)
  const loadJoinRequests = () => {
    fetch(`/api/events/${id}/join_requests/`, { credentials: 'include' })
      .then((res) => res.ok ? res.json() : [])
      .then((data) => setPendingJoinRequests(data))
      .catch((err) => console.error("Error loading join requests:", err));
  };

  // If organizer, fetch join requests periodically
  useEffect(() => {
    if (user && event && event.organizer_username === user.username) {
      loadJoinRequests();
      const interval = setInterval(loadJoinRequests, 10000); // refresh every 10 seconds
      return () => clearInterval(interval);
    }
  }, [user, event, id]);

  // Join event (creates a join request)
  const handleJoin = () => {
    if (!user) {
      window.location.href = '/accounts/signup/';
      return;
    }
    fetch(`/api/events/${id}/join/`, {
      method: 'POST',
      headers: {
        'X-CSRFToken': getCookie('csrftoken'),
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        setStatusMsg(data.status); // e.g., "Join request submitted. Awaiting organizer approval."
      })
      .catch((err) => console.error(err));
  };

  // Leave event
  const handleLeave = () => {
    if (!user) {
      window.location.href = '/accounts/signup/';
      return;
    }
    fetch(`/api/events/${id}/leave/`, {
      method: 'POST',
      headers: {
        'X-CSRFToken': getCookie('csrftoken'),
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })
      .then((res) => res.json())
      .then((data) => {
        setStatusMsg(data.status);
        return fetch(`/api/events/${id}/`, { credentials: 'include' });
      })
      .then((res) => res.json())
      .then((updated) => {
        setEvent(updated);
      })
      .catch((err) => console.error(err));
  };

  // Approve a join request
  const handleApprove = (userId) => {
    fetch(`/api/events/${id}/approve_join/`, {
      method: 'POST',
      headers: {
        'X-CSRFToken': getCookie('csrftoken'),
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ user_id: userId }),
    })
      .then((res) => res.json())
      .then((data) => {
        setStatusMsg(data.status);
        loadJoinRequests();
        fetch(`/api/events/${id}/`, { credentials: 'include' })
          .then((res) => res.json())
          .then((updated) => setEvent(updated));
      })
      .catch((err) => console.error(err));
  };

  // Reject a join request
  const handleReject = (userId) => {
    fetch(`/api/events/${id}/reject_join/`, {
      method: 'POST',
      headers: {
        'X-CSRFToken': getCookie('csrftoken'),
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ user_id: userId }),
    })
      .then((res) => res.json())
      .then((data) => {
        setStatusMsg(data.status);
        loadJoinRequests();
      })
      .catch((err) => console.error(err));
  };

  // Share event
  const handleShare = () => {
    if (!event) return;
    const shareData = {
      title: event.name,
      text: event.description,
      url: event.absolute_url || window.location.href,
    };

    if (navigator.share) {
      navigator
        .share(shareData)
        .then(() => console.log('Event shared successfully'))
        .catch((err) => console.error('Error sharing', err));
    } else {
      navigator.clipboard
        .writeText(shareData.url)
        .then(() => alert('Event link copied to clipboard!'))
        .catch((err) => console.error('Failed to copy link', err));
    }
  };

  // Delete event
  const handleDelete = () => {
    if (window.confirm('Are you sure you want to delete this event?')) {
      fetch(`/api/events/${id}/`, {
        method: 'DELETE',
        headers: {
          'X-CSRFToken': getCookie('csrftoken'),
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      })
        .then((response) => {
          if (!response.ok) {
            throw new Error('Failed to delete the event.');
          }
          alert('Event deleted successfully.');
          navigate('/');
        })
        .catch((err) => {
          console.error(err);
          alert('Failed to delete the event.');
        });
    }
  };

  // Upload event picture
  const handleUploadEventPicture = () => {
    if (!selectedFile) return;
    const formData = new FormData();
    formData.append('event_picture', selectedFile);

    fetch(`/api/events/${id}/upload-image/`, {
      method: 'POST',
      credentials: 'include',
      headers: {
        'X-CSRFToken': getCookie('csrftoken'),
      },
      body: formData,
    })
      .then(async (res) => {
        if (!res.ok) {
          const err = await res.json();
          throw err;
        }
        return res.json();
      })
      .then((data) => {
        if (data.image_url) {
          setEvent((prev) => ({ ...prev, image: data.image_url }));
          alert('Event image updated successfully!');
        } else {
          alert('Error updating event image.');
        }
      })
      .catch((err) => {
        console.error('Upload error:', err);
        alert(err.error || 'An unexpected error occurred.');
      });
  };

  // Handle form edits in state
  const handleEditChange = (e) => {
    const { name, value } = e.target;
    const numericFields = ['elevation_diff', 'distance', 'estimated_duration'];
    const parsedValue = numericFields.includes(name)
      ? value === ''
        ? null
        : parseFloat(value)
      : value;

    setEditFormData((prev) => ({ ...prev, [name]: parsedValue }));
  };

  // Submit the edit form (patch event)
  const handleEditSubmit = (e) => {
    e.preventDefault();
    const utcString = localInputValueToUtc(editFormData.event_time);
    const payload = {
      ...editFormData,
      event_time: utcString,
    };

    fetch(`/api/events/${id}/`, {
      method: 'PATCH',
      headers: {
        'X-CSRFToken': getCookie('csrftoken'),
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(payload),
    })
      .then(async (response) => {
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(JSON.stringify(errorData));
        }
        return response.json();
      })
      .then((updatedEvent) => {
        setEvent(updatedEvent);
        setStatusMsg('Event updated successfully.');
        setIsEditing(false);
      })
      .catch((err) => {
        console.error('Error updating event:', err);
        try {
          const errorObj = JSON.parse(err.message);
          const errorMessages = [];
          for (const [field, messages] of Object.entries(errorObj.errors)) {
            errorMessages.push(`${field}: ${messages.join(', ')}`);
          }
          setStatusMsg(`Failed to update event:\n${errorMessages.join('\n')}`);
        } catch {
          setStatusMsg('Failed to update event.');
        }
      });
  };

  // Chat functionality
  const loadChatMessages = () => {
    fetch(`/api/events/${id}/chat/`, { credentials: 'include' })
      .then((res) => res.json())
      .then((data) => setChatMessages(data))
      .catch((err) => console.error('Error fetching chat messages:', err));
  };

  useEffect(() => {
    loadChatMessages();
    const interval = setInterval(loadChatMessages, 5000);
    return () => clearInterval(interval);
  }, [id]);

  const handleSendMessage = () => {
    if (!newMessage.trim()) return;
    fetch(`/api/events/${id}/chat/`, {
      method: 'POST',
      headers: {
        'X-CSRFToken': getCookie('csrftoken'),
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify({ text: newMessage }),
    })
      .then((res) => res.json())
      .then(() => {
        setNewMessage('');
        loadChatMessages();
      })
      .catch((err) => console.error('Error sending chat message:', err));
  };

  const handleResetChat = () => {
    if (!window.confirm('Are you sure you want to reset the chat history?'))
      return;
    fetch(`/api/events/${id}/chat/`, {
      method: 'DELETE',
      headers: {
        'X-CSRFToken': getCookie('csrftoken'),
        'Content-Type': 'application/json',
      },
      credentials: 'include',
    })
      .then((res) => res.json())
      .then(() => {
        setChatMessages([]);
      })
      .catch((err) => console.error('Error resetting chat messages:', err));
  };

  return (
    <div className="event-detail-container">
      {!event ? (
        <div className="loading">Loading event details...</div>
      ) : (
        <>
          {/* EVENT HEADER */}
          <button onClick={() => navigate('/')} className="home-button">
            Home
          </button>
          <div className="event-header">
            <h2 className="event-title">{event.name}</h2>
            {user && event.organizer_username === user.username && (
              <button
                className="edit-icon-button"
                onClick={() => setIsEditing(!isEditing)}
                title="Edit Event"
              >
                {isEditing ? <FaTimes /> : <FaPencilAlt />}
              </button>
            )}
            <img src={event.image} alt={event.name} className="event-image" />
          </div>

          {/* EVENT INFO */}
          <div className="event-info">
            <div className="info-section">
              <strong>Event Name:</strong>
              {isEditing ? (
                <input
                  type="text"
                  name="name"
                  value={editFormData.name}
                  onChange={handleEditChange}
                  required
                />
              ) : (
                <p>{event.name}</p>
              )}
            </div>
	<div className="info-section">
	  <strong>Organizer:</strong>
	  {user ? (
	    <Link to={`/users/${event.organizer}`}>
	      {event.organizer_username}
	    </Link>
	  ) : (
	    <a href="/accounts/signup/">{event.organizer_username}</a>
	  )}
	</div>

	<div className="info-section">
	  <strong>Description:</strong>
	  {isEditing ? (
	    <textarea
	      name="description"
	      value={editFormData.description}
	      onChange={handleEditChange}
	      placeholder="Enter event description..."
	    />
	  ) : (
		  <div style={{ whiteSpace: 'pre-wrap' }}>{event.description}</div>

	  )}
	</div>

	<div className="info-section">
	  <strong>Price:</strong>
	  {isEditing ? (
	    <input
	      type="number"
	      name="price"
	      value={editFormData.price ?? ''}
	      onChange={handleEditChange}
	      min="0"
	    />
	  ) : (
	    <p>${(event.price / 100).toFixed(2)}</p>
	  )}
	</div>


            <div className="info-section">
              <strong>Event Time:</strong>
              {isEditing ? (
                <input
                  type="datetime-local"
                  name="event_time"
                  value={editFormData.event_time}
                  onChange={handleEditChange}
                  required
                />
              ) : (
                <p>{new Date(event.event_time).toLocaleString()}</p>
              )}
            </div>
	        <div className="info-section">
	  <strong>Meeting Point:</strong>
	  {isEditing ? (
	    <PlacesAutocomplete
	      value={editFormData.location}
	      onChange={(value) => setEditFormData((prev) => ({ ...prev, location: value }))}
	      onSelect={(address) => {
		setEditFormData((prev) => ({ ...prev, location: address }));
	      }}
	    >
	      {({ getInputProps, suggestions, getSuggestionItemProps, loading }) => (
		<div className="location-autocomplete-wrapper">
		  <input
		    {...getInputProps({
		      placeholder: 'Enter meeting point...',
		      className: 'location-input',
		    })}
		  />
		  <div className="autocomplete-dropdown-container">
		    {loading && <div>Loading...</div>}
		    {suggestions.map((suggestion) => {
		      const className = suggestion.active
			? 'suggestion-item--active'
			: 'suggestion-item';
		      const style = suggestion.active
			? { backgroundColor: '#a8dadc', cursor: 'pointer' }
			: { backgroundColor: '#ffffff', cursor: 'pointer' };
		      return (
			<div
			  {...getSuggestionItemProps(suggestion, { className, style })}
			  key={suggestion.placeId}
			>
			  <span>{suggestion.description}</span>
			</div>
		      );
		    })}
		  </div>
		</div>
	      )}
	    </PlacesAutocomplete>
	  ) : (
	    <>
	      <p>{event.location}</p>
	      {event.location && (
		<div className="map-container">
		  <iframe
		    title="map"
		    width="300"
		    height="200"
		    style={{ border: 0 }}
		    loading="lazy"
		    allowFullScreen
		    referrerPolicy="no-referrer-when-downgrade"
		    src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyCv5G3hiH8QN2PiRt-AtRBhBxnicJkdp78&q=${encodeURIComponent(event.location)}&zoom=14`}

		  />
		</div>
	      )}
	    </>
	  )}
	</div>



            <div className="info-section">
              <strong>Participants:</strong>
              <ul>
                {event.participants?.map((participant) => (
                  <li key={participant.id}>
                    <img
                      src={participant.profile_picture || '/default-avatar.png'}
                      alt={participant.username}
                      style={{
                        width: '30px',
                        height: '30px',
                        borderRadius: '50%',
                        marginRight: '8px',
                      }}
                    />
                    {user ? (
                      <Link to={`/users/${participant.id}`}>
                        {participant.username}
                      </Link>
                    ) : (
                	<a href="/accounts/signup/">{participant.username}</a>
                    )}
                  </li>
                ))}
              </ul>
            </div>

            <div className="info-section">
              <strong>Elevation Difference:</strong>
              {isEditing ? (
                <input
                  type="number"
                  name="elevation_diff"
                  value={editFormData.elevation_diff ?? ''}
                  onChange={handleEditChange}
                  required
                  min="0"
                />
              ) : (
                <p>{event.elevation_diff} meters</p>
              )}
            </div>

            <div className="info-section">
              <strong>Distance:</strong>
              {isEditing ? (
                <input
                  type="number"
                  name="distance"
                  value={editFormData.distance ?? ''}
                  onChange={handleEditChange}
                  required
                  min="0"
                />
              ) : (
                <p>{event.distance} kilometers</p>
              )}
            </div>

            <div className="info-section">
              <strong>Expected Duration:</strong>
              {isEditing ? (
                <input
                  type="number"
                  name="estimated_duration"
                  value={editFormData.estimated_duration ?? ''}
                  onChange={handleEditChange}
                  required
                  min="0"
                  step="0.1"
                />
              ) : (
                <p>{event.estimated_duration} hours (approx)</p>
              )}
            </div>

            {isEditing && (
              <div className="edit-actions">
                <button className="save-button" onClick={handleEditSubmit}>
                  <FaSave /> Save
                </button>
                <button
                  className="cancel-button"
                  onClick={() => setIsEditing(false)}
                >
                  <FaTimes /> Cancel
                </button>
              </div>
            )}
          </div>

          {/* ACTION BUTTONS */}
          <div className="buttons-section">
            <button className="join-button" onClick={handleJoin}>
              Join Event
            </button>
            <button className="leave-button" onClick={handleLeave}>
              Leave Event
            </button>
            {user && event.organizer_username === user.username && (
              <button className="delete-button" onClick={handleDelete}>
                Delete Event
              </button>
            )}
          </div>

          {/* NEW: Pending Join Requests Section (visible only to the organizer) */}
          {user && event.organizer_username === user.username && (
            <div className="join-requests-section">
              <h3>Pending Join Requests</h3>
              {pendingJoinRequests.length === 0 ? (
                <p>No pending join requests.</p>
              ) : (
                <ul>
                  {pendingJoinRequests.map((req) => (
			  <li key={req.id} className="join-request-item">
			  <img
			    src={req.user.profile_picture || '/default-avatar.png'}
			    alt={req.user.username}
			    style={{
			      width: '30px',
			      height: '30px',
			      borderRadius: '50%',
			      marginRight: '8px',
			    }}
			  />
			  <Link to={`/users/${req.user.id}`}>
			    {req.user.username}
			  </Link>
			  <button onClick={() => handleApprove(req.user.id)}>Approve</button>
			  <button onClick={() => handleReject(req.user.id)}>Reject</button>
			</li>

                  ))}
                </ul>
              )}
            </div>
          )}

          {/* SHARE SECTION */}
          <div className="share-container">
            <button
              onClick={() => setShowShare(!showShare)}
              className="share-icon-button"
              title="Share Event"
            >
              🔗 Share
            </button>
            {showShare && (
              <div className="share-popup">
                <input
                  type="text"
                  value={eventUrl}
                  readOnly
                  className="share-input"
                />
                <button
                  onClick={() => {
                    navigator.clipboard
                      .writeText(eventUrl)
                      .then(() => alert('Event URL copied!'))
                      .catch((err) =>
                        console.error('Error copying text:', err)
                      );
                  }}
                  className="copy-button"
                >
                  Copy URL
                </button>
              </div>
            )}
          </div>

          {/* EVENT PICTURE UPLOAD (ORGANIZER ONLY) */}
          {user && event.organizer_username === user.username && (
            <div className="profile-picture-section">
              <h4>Change Event Picture</h4>
              <input type="file" onChange={handleFileSelect} />
              <button
                onClick={handleUploadEventPicture}
                className="upload-button"
              >
                Upload
              </button>
            </div>
          )}

	{user && user.id !== event.organizer && (
	  <button className="payment-button" onClick={() => setShowPayment(true)}>
	    Pay for Event
	  </button>
	)}


	{showPayment && (
	  <div className="modal">
	    <div className="modal-content">
	      <button onClick={() => setShowPayment(false)}>Close</button>
	      <PaymentComponent
		eventId={event.id}
		amountInCents={1200} // or a dynamically calculated amount
		onSuccess={() => {
		  // ...
		  fetch(`/api/events/${event.id}/mark-paid/`, {
		    method: 'POST',
		    headers: {
		      'X-CSRFToken': getCookie('csrftoken'),
		      'Content-Type': 'application/json',
		    },
		    credentials: 'include',
		  })
		  .then(res => res.json())
		  .then(data => {
		    console.log(data.status); // e.g., "Payment recorded"
		    alert('Payment successful!');
		    setShowPayment(false);
		  });
		}}

		/*
		onSuccess={() => {
		  alert('Payment successful!');
		  setShowPayment(false);
		}}*/
	      />
	    </div>
	  </div>
	)}

		
          {/* EVENT CHAT SECTION */}
		{user && (event.participants?.some((p) => p.id === user.id) || event.organizer === user.id) ? (
		  <div className="chat-container">
		    <h3>Event Chat</h3>
		    <div className="chat-messages">
		      {chatMessages.map((msg) => (
			<div key={msg.id} className="chat-message">
			  <strong>{msg.username}:</strong> {msg.text}
			  <span className="timestamp">
			    {new Date(msg.timestamp).toLocaleTimeString()}
			  </span>
			</div>
		      ))}
		    </div>
		    <div className="chat-input">
		      <input
			type="text"
			value={newMessage}
			placeholder="Type your message..."
			onChange={(e) => setNewMessage(e.target.value)}
		      />
		      <button onClick={handleSendMessage}>Send</button>
		    </div>
		    {user && event.organizer_username === user.username && (
		      <button className="reset-chat-button" onClick={handleResetChat}>
			Reset Chat
		      </button>
		    )}
		  </div>
		) : (
		  <p>Chat is available only for event participants or the organizer.</p>
		)}
		

          {/* STATUS MESSAGE */}
          {statusMsg && <p className="status-message">{statusMsg}</p>}
        </>
      )}
    </div>
  );
}

export default EventDetail;

